import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Layout as AntLayout,
  Menu,
  Dropdown,
  Avatar,
  Button,
  Modal,
  Drawer,
  Tabs
} from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LockOutlined,
  ApiOutlined,
  EllipsisOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import BackendConnectionSettings from './BackendConnectionSettings';
import { PasswordSettingsModal } from './AccountSecurity';
import { getNavigation } from '../utils/navigation';
import { NavigationContext } from './NavigationGuard';

const { Header, Sider, Content } = AntLayout;

function Layout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [mobile, setMobile] = useState(() => window.innerWidth < 992);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const [unsaved, setUnsaved] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasPermission } = useAuth();

  useEffect(() => {
    const resize = () => setMobile(window.innerWidth < 992);
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname, location.search]);

  const sections = getNavigation(user, hasPermission);
  const parent = sections.find(section => section.children?.some(child => child.path === location.pathname));
  const current = parent?.children.find(item => item.path === location.pathname) || sections.find(item => item.path === location.pathname);
  const selected = parent?.path || current?.path;
  const go = path => {
    if (path === location.pathname + location.search) return;
    if (unsaved) Modal.confirm({ title: '有未保存的修改', content: '离开后当前未保存的配置会丢失。', okText: '离开页面', cancelText: '继续编辑',
      onOk: () => { setUnsaved(false); navigate(path); } });
    else navigate(path);
  };
  useEffect(() => {
    if (!unsaved) return;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [unsaved]);
  const navigation = <Menu theme="dark" mode="inline" selectedKeys={selected ? [selected] : []}
    items={sections.map(section => ({ key: section.path, label: section.label, icon: section.icon }))}
    onClick={({ key }) => { const section = sections.find(item => item.path === key); go(section.children?.[0]?.path || section.path); }} />;

  const handleLogout = () => {
    Modal.confirm({
      title: '确认退出',
      content: '您确定要退出登录吗？',
      onOk: logout
    });
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人资料',
      onClick: () => go('/profile')
    },
    {
      key: 'change-password',
      icon: <LockOutlined />,
      label: '修改密码',
      onClick: () => setPasswordModalVisible(true)
    },
    {
      key: 'connection',
      icon: <ApiOutlined />,
      label: '连接诊断',
      onClick: () => setDiagnosticsOpen(true)
    },
    {
      type: 'divider'
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout
    }
  ];

  const getRoleName = (role) => {
    const roleNames = {
      'super_admin': '超级管理员',
      'admin': '管理员',
      'user': '普通用户'
    };
    return roleNames[role] || role;
  };

  return (
    <NavigationContext.Provider value={{ setUnsaved, navigate: go }}><AntLayout className="app-shell">
      {!mobile && <Sider className="app-sidebar" width={232} trigger={null} collapsible collapsed={collapsed}>
        <div className="logo" title="轻锋机器人管理后台">
          {collapsed ? '轻锋' : '轻锋机器人管理后台'}
        </div>
        {navigation}
      </Sider>}
      <Drawer title="轻锋机器人管理后台" placement="left" width={260} open={mobile && mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)} styles={{ body: { padding: '12px 0', background: '#001529' } }}>
        {navigation}
      </Drawer>
      
      <AntLayout className="app-main">
        <Header className="app-header">
          <Button
            type="text"
            aria-label={mobile ? '打开导航菜单' : collapsed ? '展开导航菜单' : '收起导航菜单'}
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => mobile ? setMobileMenuOpen(true) : setCollapsed(!collapsed)}
            style={{
              fontSize: '16px',
              width: 44,
              height: 44,
              flexShrink: 0,
            }}
          />
          {parent ? (
            <nav className="module-navigation" aria-label={`${parent.label}子菜单`}>
              <Tabs activeKey={location.pathname} onChange={go} tabBarGutter={24}
                more={{ trigger: 'click', icon: <EllipsisOutlined aria-label="更多子菜单" /> }}
                items={parent.children.map(item => ({ key: item.path, label: <span className="module-tab-label">{item.icon}<span>{item.label}</span></span> }))} />
            </nav>
          ) : (
            <div className="header-page-title">{current?.icon || <UserOutlined />}<span>{current?.label || '个人资料'}</span></div>
          )}
          
          <div className="header-right">
            <span className="user-info">
              <span className="user-name" title={user?.username}>{user?.username}</span><span className="user-role">{getRoleName(user?.role)}</span>
            </span>
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              trigger={['hover', 'click']}
              arrow
            >
              <Button type="text" className="account-button" aria-label="账户菜单"><Avatar size="small" icon={<UserOutlined />} /></Button>
            </Dropdown>
          </div>
        </Header>
        
        <Content className="app-content" id="main-content">
          {children}
        </Content>
      </AntLayout>

      <Modal title="连接诊断" open={diagnosticsOpen} onCancel={() => setDiagnosticsOpen(false)} footer={null}>
        <p>页面无法连接服务时，可检查当前浏览器使用的后端地址。</p><BackendConnectionSettings />
      </Modal>

      <PasswordSettingsModal open={passwordModalVisible} onClose={() => setPasswordModalVisible(false)} />
    </AntLayout></NavigationContext.Provider>
  );
}

export default Layout;
